export interface PassioToken {
  accessToken: string;
  customerId: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

/** How a client authenticates. Implementations add headers and may refresh on 401. */
export interface PassioAuth {
  headers(signal?: AbortSignal): Promise<Record<string, string>>;
  /** Drop cached credentials; the client calls this once on 401 and retries. */
  invalidate?(): void;
  /** Fetch credentials mode (cookies for same-origin proxies). */
  readonly credentials?: RequestCredentials;
}

/** The license key could not be exchanged for a token (`status` is the exchange endpoint's HTTP status). */
export class PassioTokenError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "PassioTokenError";
  }
}

export interface LicenseKeyAuthOptions {
  licenseKey: string;
  /** API host without path, e.g. `https://api.passiolife.com` or `https://api-dev.passiolife.com`. */
  host?: string;
  fetch?: typeof fetch;
  /** Refresh this many seconds before expiry. */
  refreshSkewSeconds?: number;
}

/**
 * Exchanges a license key for a bearer token
 * (`GET {host}/v2/token-cache/unified/oauth/token/{key}`) and sends
 * `Authorization` + `Passio-ID` on every call. Server-side use only: the key is a secret.
 */
export class LicenseKeyAuth implements PassioAuth {
  private readonly licenseKey: string;
  private readonly host: string;
  private readonly fetchImpl: typeof fetch;
  private readonly skewMs: number;
  private token?: PassioToken;
  private inflight?: Promise<PassioToken>;

  constructor(opts: LicenseKeyAuthOptions) {
    if (!opts.licenseKey) throw new Error("LicenseKeyAuth: licenseKey is required");
    this.licenseKey = opts.licenseKey;
    this.host = (opts.host ?? "https://api.passiolife.com").replace(/\/+$/, "");
    this.fetchImpl = opts.fetch ?? ((...args) => fetch(...args));
    this.skewMs = (opts.refreshSkewSeconds ?? 60) * 1000;
  }

  async getToken(signal?: AbortSignal): Promise<PassioToken> {
    if (this.token && this.token.expiresAt - this.skewMs > Date.now()) return this.token;
    this.inflight ??= this.exchange(signal).finally(() => (this.inflight = undefined));
    return this.inflight;
  }

  async headers(signal?: AbortSignal): Promise<Record<string, string>> {
    const t = await this.getToken(signal);
    return { Authorization: `Bearer ${t.accessToken}`, "Passio-ID": t.customerId };
  }

  invalidate(): void {
    this.token = undefined;
  }

  private async exchange(signal?: AbortSignal): Promise<PassioToken> {
    const url = `${this.host}/v2/token-cache/unified/oauth/token/${encodeURIComponent(this.licenseKey)}`;
    const res = await this.fetchImpl(url, { signal });
    if (!res.ok) throw new PassioTokenError(`Passio token exchange failed: HTTP ${res.status}`, res.status);
    const body = (await res.json().catch(() => ({}))) as { access_token?: string; expires_in?: number; customer_id?: string };
    if (!body.access_token || !body.customer_id) throw new PassioTokenError("Passio token exchange returned no access_token/customer_id", res.status);
    this.token = {
      accessToken: body.access_token,
      customerId: body.customer_id,
      expiresAt: Date.now() + (Number(body.expires_in) || 3600) * 1000,
    };
    return this.token;
  }
}

/** For browsers talking to a same-origin proxy that injects credentials server-side. */
export class ProxyAuth implements PassioAuth {
  readonly credentials: RequestCredentials = "same-origin";
  async headers(): Promise<Record<string, string>> {
    return {};
  }
}

/** A pre-issued bearer token (e.g. minted by your backend). */
export class StaticTokenAuth implements PassioAuth {
  constructor(
    private readonly accessToken: string,
    private readonly customerId: string,
  ) {}
  async headers(): Promise<Record<string, string>> {
    return { Authorization: `Bearer ${this.accessToken}`, "Passio-ID": this.customerId };
  }
}
